// How dates read on this device: Nepali (BS), English (AD) or both. A per-device
// convenience kept in localStorage, like the theme; with nothing chosen the
// business's own setting applies (Settings → Show dates in). Printed bills never
// follow it: a bill must read the same whoever printed it.
import type { DateDisplay } from '@ims/core'
import { useSyncExternalStore } from 'react'

const KEY = 'ims.dates'
const listeners = new Set<() => void>()

function read(): DateDisplay | null {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'bs' || value === 'ad' || value === 'both' ? value : null
  } catch {
    return null
  }
}

let current: DateDisplay | null = read()

/** `null` goes back to the business's setting. */
export function setDateChoice(choice: DateDisplay | null) {
  current = choice
  try {
    if (choice) localStorage.setItem(KEY, choice)
    else localStorage.removeItem(KEY)
  } catch {
    // Storage blocked: the choice still applies for this session.
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** This device's choice, or null while it follows the business. */
export function useDateChoice(): DateDisplay | null {
  return useSyncExternalStore(subscribe, () => current)
}

/**
 * The settings as this device shows them: `date_display` is the device's choice, and the
 * business's own value is kept beside it for printing and for the Settings screen.
 */
export function withDateChoice<T extends { date_display: DateDisplay }>(
  settings: T,
  choice: DateDisplay | null,
): T & { business_date_display?: DateDisplay } {
  if (!choice || choice === settings.date_display) return settings
  return { ...settings, date_display: choice, business_date_display: settings.date_display }
}

/** The business's setting, whatever this device has chosen. */
export function businessDateDisplay(
  settings: { date_display: DateDisplay; business_date_display?: DateDisplay } | null | undefined,
): DateDisplay {
  return settings?.business_date_display ?? settings?.date_display ?? 'both'
}
